import { HelpTopic } from './helpContent';

export const krPcHelpTopics: HelpTopic[] = [
  {
    id: 'krpc-overview',
    label: 'Overview',
    title: 'Kr × Pc Model Page',
    intro:
      'The Kr × Pc Model page is the final step of the SCAL workflow. It combines capillary pressure and relative permeability data from individual samples — grouped by flow unit zones — into a unified system model for each zone. The output is a set of continuous curves ready for reservoir simulator input.',
    sections: [
      {
        id: 'overview-purpose',
        title: 'Purpose',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Generates continuous Pcow, Krw, Krow, Krg, and Krog curves per zone across the full saturation range.',
              'Provides ready-to-export Kr and Pc tables for each zone, formatted for reservoir simulator input.',
              'Enables visual comparison of flow behavior between zones.',
              'Links pore-scale SCAL measurements to reservoir-scale saturation and height modeling.',
            ],
          },
        ],
      },
      {
        id: 'overview-models',
        title: 'Two model variants',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Liao model',
                description: 'Three-parameter capillary pressure fit using best_a, best_b, and lambda_variable from the Liao methodology.',
                effect: 'Preferred for rocks with complex pore size distributions (mixed-wet or variable throat sizes). Curves may show more complex shapes at intermediate saturations.',
              },
              {
                name: 'Brooks-Corey model',
                description: 'Two-parameter power-law fit using Pe and the fitted exponent m from the Brooks-Corey methodology. The classic pore-size distribution index is λBC = −1/m.',
                effect: 'Preferred for cleaner, more uniform rocks. Curves always follow the power-law form — simpler and more symmetric.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Kr curves are identical between models',
            text: 'The model toggle only affects the Pc curves. Both Liao and Brooks-Corey use the same Corey power-law equations for Krw, Krow, Krg, and Krog. Switch between models to see which Pc fit produces the most physically consistent curves for your reservoir.',
          },
        ],
      },
      {
        id: 'overview-resolution',
        title: 'Curve resolution control',
        blocks: [
          {
            type: 'bullets',
            items: [
              'The number of curve points (10–100, default 50) controls the resolution of all generated curves.',
              'Higher resolution produces smoother curves but takes slightly longer to compute.',
              '50 points is sufficient for most visualization and export purposes.',
              'Use 100 points for publication-quality output or before exporting to a simulator.',
            ],
          },
        ],
      },
      {
        id: 'overview-prereqs',
        title: 'Workflow prerequisites',
        blocks: [
          {
            type: 'paragraph',
            text: 'This page reads existing zone_values and renders results automatically — it requires no user input. However, all upstream steps must be complete before meaningful curves appear:',
          },
          {
            type: 'bullets',
            items: [
              'Sample analyses completed: Liao or Brooks-Corey fits on the Capillary Pressure page, Corey fits on the Kr Analysis page.',
              'Correlation run: FZI regression functions stored per property on the Correlation page.',
              'Zones created: zone.mean (FZI) used as x-input to regression functions, zone_values populated on the Zones page.',
            ],
          },
          {
            type: 'note',
            title: 'Empty curves?',
            text: 'If any chart is blank, return to the Zones page and confirm that zone_values are non-empty. If empty, re-run the correlation on the Correlation page with representative samples selected.',
          },
        ],
      },
    ],
  },
  {
    id: 'krpc-systems',
    label: 'Fluid Systems',
    title: 'Water-Oil and Gas-Oil Systems',
    intro:
      'The Kr × Pc Model generates curves for two fluid displacement systems. Each system uses its own set of zone parameters and produces a distinct pair of Kr curves.',
    sections: [
      {
        id: 'systems-wo',
        title: 'Water-Oil system',
        blocks: [
          {
            type: 'paragraph',
            text: 'Describes displacement of oil by water injection (imbibition). This is the primary system in most SCAL workflows.',
          },
          {
            type: 'bullets',
            items: [
              'Produces Pcow (capillary pressure), Krw (water), and Krow (oil) as functions of water saturation (Sw).',
              'Height above free water level is derived from Pcow.',
              'Pc curves are generated using either the Liao or Brooks-Corey model depending on the selected toggle.',
            ],
          },
        ],
      },
      {
        id: 'systems-go',
        title: 'Gas-Oil system',
        blocks: [
          {
            type: 'paragraph',
            text: 'Describes gas injection or solution gas drive (drainage). The gas-oil system produces Kr curves as functions of gas saturation (Sg).',
          },
          {
            type: 'bullets',
            items: [
              'Produces Krg (gas) and Krog (oil) as functions of gas saturation (Sg).',
              'Uses a separate Pc_max_og parameter for gas-oil capillary pressure — not the Liao or Brooks-Corey Pc model.',
              'Gas-oil Kr parameters (Krg\', Ng, No_g, Sgc) have standard defaults and may not be directly measured in SCAL.',
            ],
          },
          {
            type: 'note',
            title: 'Default gas-oil parameters',
            text: 'Krg\' defaults to 0.9, Ng to 6.0, No_g to 3.0, and Sgc to 0.02 when gas-oil Kr measurements are not available from the Kr Analysis page. If measured gas-oil data exists, those values are used instead. Gas-oil curves may look similar across zones when only defaults are applied.',
          },
        ],
      },
      {
        id: 'systems-shared',
        title: 'Shared parameters between systems',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Swir (irreducible water saturation) — appears in both water-oil and gas-oil Kr equations.',
              'kro_absolute (oil endpoint relative permeability) — shared endpoint used in both Krow and Krog equations.',
              'Sor_w (residual oil saturation in water-oil) — also used in the KROG equation.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'krpc-pc',
    label: 'Capillary Pressure',
    title: 'Capillary Pressure Curves (Pcow)',
    intro:
      'Capillary pressure curves describe the pressure difference between the non-wetting phase (oil) and the wetting phase (water) as a function of water saturation. Two models are available, both driven by zone_values interpolated from FZI regression.',
    sections: [
      {
        id: 'pc-liao',
        title: 'Liao Capillary Pressure Model',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Equation',
                description: 'Pcow(Sw) = ((1 + a) × (Pc_max − Pce)) / (b × SwN + 1)^(1/λ) + Pce − a × (Pc_max − Pce)',
                effect: 'Where SwN = (Sw − Swir) / (1 − Swir) is the normalized water saturation.',
              },
              {
                name: 'a, b (best_a, best_b)',
                description: 'Liao model parameters fitted from individual sample Pc curves, then interpolated per zone via FZI regression.',
                effect: 'Control the asymmetry and steepness of the Pc curve across the saturation range.',
              },
              {
                name: 'Pce (capillary entry pressure)',
                description: 'Minimum Pcow — the pressure required for the non-wetting phase to enter the largest pore throats.',
                effect: 'Sets the lower bound of the Pc curve. Higher Pce indicates tighter pore throats.',
              },
              {
                name: 'Pc_max (maximum capillary pressure)',
                description: 'Maximum Pcow at Sw = Swir — the pressure required to hold oil when water is at irreducible saturation.',
                effect: 'Sets the upper bound of the Pc curve. Zones with higher Pc_max represent tighter, lower-quality rock.',
              },
              {
                name: 'λ (lambda_variable)',
                description: 'Pore size distribution indicator derived from a, b, and Pce.',
                effect: 'Higher λ → more gradual drainage curve. Lower λ → steeper transition from Pc_max toward Pce.',
              },
            ],
          },
          {
            type: 'note',
            title: 'When to use Liao',
            text: 'Use the Liao model when samples were fitted with the Liao methodology on the Capillary Pressure page. It is preferred for rocks with complex pore size distributions (mixed-wet or variable throat sizes).',
          },
        ],
      },
      {
        id: 'pc-lambda',
        title: 'Brooks-Corey Capillary Pressure Model',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Fitted basis',
                description: 'Pc = Pe × Swn^m, where m is the fitted negative drainage exponent. The classic equivalent form is Pc = Pe × Swn^(−1/λBC).',
                effect: 'Power-law relationship between Pc and normalized water saturation.',
              },
              {
                name: 'Pe (entry pressure)',
                description: 'Capillary entry pressure from the Brooks-Corey fit.',
                effect: 'Sets the lower bound of the Pc curve. Analogous to Pce in the Liao model.',
              },
              {
                name: 'Fitted exponent m and λBC',
                description: 'm is the negative log-log slope fitted by the analysis. The classic positive pore-size distribution index is λBC = −1/m.',
                effect: 'More-negative m corresponds to smaller λBC and a sharper Pc rise toward Swir.',
              },
            ],
          },
          {
            type: 'note',
            title: 'When to use Brooks-Corey',
            text: 'Use the Brooks-Corey model when samples were fitted with the Brooks-Corey methodology. It is preferred for cleaner, more uniform rocks where the power-law model fits well.',
          },
        ],
      },
    ],
  },
  {
    id: 'krpc-kr',
    label: 'Relative Permeability',
    title: 'Relative Permeability Curves',
    intro:
      'Both Liao and Brooks-Corey models use identical Corey power-law equations for Kr. The model toggle only affects the Pc curves. All Kr curves are generated directly from zone_values interpolated via FZI regression.',
    sections: [
      {
        id: 'kr-wo',
        title: 'Water-Oil Relative Permeability',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Krw(Sw) = Krw\' × ((Sw − Swir) / (1 − Swir − Sor_w))^Nw',
                description: 'Water relative permeability as a function of water saturation.',
                effect: 'At Sw = Swir: Krw = 0 (water immobile). At Sw = 1 − Sor_w: Krw = Krw\' (water at maximum mobility).',
              },
              {
                name: 'Krow(Sw) = Kro\' × ((1 − Sw − Sor_w) / (1 − Swir − Sor_w))^No_w',
                description: 'Oil relative permeability in the water-oil system.',
                effect: 'At Sw = Swir: Krow = Kro\' (oil at maximum mobility). At Sw = 1 − Sor_w: Krow = 0 (oil immobile).',
              },
            ],
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Krw\' (krw_absolute)',
                description: 'Water endpoint relative permeability — maximum Krw at Sor_w.',
                effect: 'Shifts the entire Krw curve up or down. Typically 0.3–0.5 for water-wet rock.',
              },
              {
                name: 'Kro\' (kro_absolute)',
                description: 'Oil endpoint relative permeability — maximum Krow at Swir.',
                effect: 'Shared between water-oil and gas-oil systems. Values below 1.0 signal wettability effects.',
              },
              {
                name: 'Nw',
                description: 'Water Corey exponent.',
                effect: 'Controls Krw curve curvature. Higher Nw delays the rise of Krw toward higher saturations.',
              },
              {
                name: 'No_w',
                description: 'Oil Corey exponent for water-oil system.',
                effect: 'Controls Krow curvature. Higher No_w keeps Krow near its maximum over a wider range before dropping near Sor_w.',
              },
              {
                name: 'Swir',
                description: 'Irreducible water saturation — lower Sw bound.',
                effect: 'Defines where Krw = 0 and Krow is at maximum. Shared with gas-oil system.',
              },
              {
                name: 'Sor_w',
                description: 'Residual oil saturation in water-oil system — upper Sw bound.',
                effect: 'Defines where Krow = 0 and Krw is at maximum.',
              },
            ],
          },
        ],
      },
      {
        id: 'kr-go',
        title: 'Gas-Oil Relative Permeability',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Krg(Sg) = Krg\' × ((Sg − Sgc) / (1 − Sgc − Swir − Sor_g))^Ng',
                description: 'Gas relative permeability as a function of gas saturation.',
                effect: 'At Sg = Sgc: Krg = 0 (gas immobile below critical saturation). At Sg = 1 − Swir − Sor_g: Krg = Krg\'.',
              },
              {
                name: 'Krog(Sg) = Kro\' × ((1 − Sg − Swir − Sor_w) / (1 − Swir − Sor_w))^No_w',
                description: 'Oil relative permeability in the gas-oil system.',
                effect: 'Uses the shared Kro\' and No_w from the water-oil system. Decreases as gas displaces oil.',
              },
            ],
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Krg\' (krg_absolute)',
                description: 'Gas endpoint relative permeability. Default: 0.9.',
                effect: 'Maximum gas mobility at high Sg. Overridden if gas-oil Kr measurements are available.',
              },
              {
                name: 'Ng',
                description: 'Gas Corey exponent. Default: 6.0.',
                effect: 'Controls Krg curve curvature.',
              },
              {
                name: 'No_g',
                description: 'Oil Corey exponent for gas-oil system. Default: 3.0.',
                effect: 'Controls Krog curvature.',
              },
              {
                name: 'Sgc',
                description: 'Critical gas saturation. Default: 0.02.',
                effect: 'Minimum Sg below which gas does not flow. Gas must exceed Sgc to appear in Krg calculations.',
              },
              {
                name: 'Sor_g',
                description: 'Residual oil saturation in gas-oil system.',
                effect: 'Defines the upper bound of mobile gas saturation.',
              },
            ],
          },
        ],
      },
      {
        id: 'kr-scaled',
        title: 'Scaled vs. Absolute Kr',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Absolute Kr',
                description: 'Raw relative permeability values — Krw, Krow, Krg, Krog — each ranging from 0 to their respective endpoints (Krw\', Kro\', Krg\').',
                effect: 'Use to compare endpoint magnitudes between zones. Shows the actual Kr values as they would be entered into a simulator.',
              },
              {
                name: 'Scaled Kr',
                description: 'All Kr values normalized by dividing by kro_absolute: Kr_scaled = Kr / Kro\'.',
                effect: 'Scaled curves converge to the same oil endpoint (1.0 at Swir) regardless of each zone\'s kro_absolute. Use to compare zone shapes without endpoint magnitude effects. Standard format required by most reservoir simulators.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'krpc-height',
    label: 'Height Function',
    title: 'Height Above Free Water Level',
    intro:
      'The height function converts capillary pressure into a vertical height above the Free Water Level (FWL). It is the foundation for saturation-height functions (SHF) used in static reservoir model initialization.',
    sections: [
      {
        id: 'height-definition',
        title: 'Height equation',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Height(Sw) = Pcow(Sw) / grad_diff',
                description: 'Converts capillary pressure to vertical height above FWL.',
                effect: 'grad_diff is the density difference between water and oil gradients (default 0.075 psi/ft or equivalent). Higher grad_diff compresses the transition zone; lower grad_diff expands it.',
              },
            ],
          },
        ],
      },
      {
        id: 'height-interpretation',
        title: 'What the Height vs. Sw curve shows',
        blocks: [
          {
            type: 'bullets',
            items: [
              'At Swir: maximum height — oil column height needed to reduce water to irreducible saturation.',
              'As height decreases toward zero: water saturation increases toward full water saturation.',
              'The curve shape and scale depend directly on the Pc curve and grad_diff.',
              'Zones with higher Pc_max have taller transition zones.',
              'Zones with steeper Pc curves (higher λ) have thinner transition zones.',
            ],
          },
        ],
      },
      {
        id: 'height-usage',
        title: 'How to use for reservoir modeling',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Each zone\'s height curve defines how Sw varies with depth in a column of that rock type.',
              'These curves become the saturation-height function (SHF) inputs for static model initialization.',
              'High-quality zones (high FZI) transition at lower heights — they drain more easily.',
              'Low-quality zones (low FZI) need greater height to reduce water saturation — thick transition zones.',
              'Compare zones on the Height chart to validate that the zone ordering matches geological reservoir quality expectations.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'krpc-charts',
    label: 'Charts',
    title: 'Charts — Interpretation Guide',
    intro:
      'The Kr × Pc Model page displays four charts, each targeting a specific aspect of the unified system model. Each zone is represented by a curve in the zone\'s assigned color.',
    sections: [
      {
        id: 'chart-pcow',
        title: 'Pcow vs. Sw — Capillary Pressure',
        blocks: [
          {
            type: 'paragraph',
            text: 'Shows capillary pressure as a function of water saturation — one curve per zone.',
          },
          {
            type: 'bullets',
            items: [
              'Zones with high Pc_max have large entry pressures — they represent tighter, lower-quality rock.',
              'Zones with low Pc_max have smaller entry pressures — better-quality, higher-permeability rock.',
              'Crossovers between zone curves reveal the transition from one flow unit to another.',
              'Steep curves (fast Pc drop with increasing Sw) indicate well-sorted pore systems (high λ).',
              'Flat, gradual curves indicate heterogeneous pore systems (low λ).',
              'Liao curves may show more complex shapes at intermediate saturations; Brooks-Corey curves always follow the power-law form.',
            ],
          },
        ],
      },
      {
        id: 'chart-height',
        title: 'Height vs. Sw — Saturation-Height Function',
        blocks: [
          {
            type: 'paragraph',
            text: 'Shows height above Free Water Level vs. water saturation — the direct input for static model initialization.',
          },
          {
            type: 'bullets',
            items: [
              'The height at Swir tells you how thick the transition zone is for each rock type.',
              'High-quality zones (high FZI) transition at lower heights — they drain more easily.',
              'Low-quality zones (low FZI) need greater height to reduce water saturation — thick transition zones.',
              'These curves become the saturation-height function (SHF) inputs for static model initialization.',
              'Each zone\'s curve defines how Sw varies with depth in a column of that rock type.',
            ],
          },
        ],
      },
      {
        id: 'chart-kr-wo',
        title: 'Kr vs. Sw — Water-Oil Relative Permeability',
        blocks: [
          {
            type: 'paragraph',
            text: 'Shows Krw and Krow (or their scaled equivalents) vs. water saturation — one pair of curves per zone.',
          },
          {
            type: 'bullets',
            items: [
              'Crossover point (Sw where Krw = Krow) — indicates wettability. Sw < 0.5 signals oil-wet/mixed-wet; Sw > 0.5 signals water-wet.',
              'Shape of Krow: steep drop = water-wet; gradual = oil-wet.',
              'Zones with higher FZI typically have lower Swir and different crossover behavior.',
              'Use absolute Kr to compare endpoint magnitudes between zones.',
              'Use scaled Kr to compare curve shapes without endpoint magnitude effects.',
            ],
          },
        ],
      },
      {
        id: 'chart-kr-go',
        title: 'Kr vs. Sg — Gas-Oil Relative Permeability',
        blocks: [
          {
            type: 'paragraph',
            text: 'Shows Krg and Krog vs. gas saturation — one pair of curves per zone.',
          },
          {
            type: 'bullets',
            items: [
              'Sgc (critical gas saturation): the point where Krg first becomes non-zero.',
              'Endpoint Krg\': maximum gas mobility at high Sg.',
              'Krog shape: shows how oil mobility decreases as gas displaces it.',
              'Gas-oil curves are often similar across zones if only default gas-oil parameters are used.',
              'Meaningful differentiation between zones requires measured gas-oil Kr data from the Kr Analysis page.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'krpc-tables',
    label: 'Tables',
    title: 'Zone Parameter Tables',
    intro:
      'Two parameter tables display all zone_values used to generate the curves. These tables are the direct link between zone regression results and the computed curves — if a curve looks wrong, start here.',
    sections: [
      {
        id: 'table-wo',
        title: 'Water-Oil Table',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Zone',
                description: 'Zone name and color indicator.',
                effect: 'Matches the curve colors in all four charts.',
              },
              {
                name: 'FZI Mean',
                description: 'The zone mean FZI used as x-input to the regression equations.',
                effect: 'All zone_values are derived from this value via the FZI correlation functions.',
              },
              {
                name: 'Swir',
                description: 'Irreducible water saturation.',
                effect: 'Lower Sw bound for Kr equations. Shared with gas-oil system.',
              },
              {
                name: 'Sor_w',
                description: 'Residual oil saturation (water-oil).',
                effect: 'Upper Sw bound for Kr equations. Defines where Krow = 0.',
              },
              {
                name: 'Kro\'',
                description: 'Oil endpoint relative permeability.',
                effect: 'Shared between water-oil and gas-oil systems.',
              },
              {
                name: 'Krw\'',
                description: 'Water endpoint relative permeability.',
                effect: 'Maximum Krw at Sor_w.',
              },
              {
                name: 'No',
                description: 'Oil Corey exponent for water-oil system.',
                effect: 'Controls Krow curvature.',
              },
              {
                name: 'Nw',
                description: 'Water Corey exponent.',
                effect: 'Controls Krw curvature.',
              },
              {
                name: 'Pce / Pe',
                description: 'Capillary entry pressure (Liao: Pce; Brooks-Corey: Pe).',
                effect: 'Lower bound of the Pc curve. Higher values indicate tighter pore throats.',
              },
              {
                name: 'Pc_max',
                description: 'Maximum capillary pressure at Swir.',
                effect: 'Upper bound of the Pc curve.',
              },
              {
                name: 'Liao lambda / Brooks-Corey m and λBC',
                description: 'Liao lambda is derived from a, b and Pce. For Brooks-Corey, m is the fitted negative exponent and λBC = −1/m is the classic pore-size distribution index.',
                effect: 'Controls Pc curve steepness.',
              },
              {
                name: 'best_a / best_b',
                description: 'Liao model parameters (only visible in Liao model view).',
                effect: 'Used in the Liao Pc equation to define curve shape.',
              },
            ],
          },
        ],
      },
      {
        id: 'table-go',
        title: 'Gas-Oil Table',
        blocks: [
          {
            type: 'paragraph',
            text: 'Contains the same zone list with gas-oil system parameters.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Sgc',
                description: 'Critical gas saturation.',
                effect: 'Minimum Sg below which gas does not flow.',
              },
              {
                name: 'Sor_g',
                description: 'Residual oil saturation (gas-oil).',
                effect: 'Upper bound of mobile gas saturation.',
              },
              {
                name: 'Krg\'',
                description: 'Gas endpoint relative permeability.',
                effect: 'Maximum Krg at high Sg. Default 0.9 when not measured.',
              },
              {
                name: 'Ng',
                description: 'Gas Corey exponent.',
                effect: 'Controls Krg curvature. Default 6.0.',
              },
              {
                name: 'No_g',
                description: 'Oil Corey exponent for gas-oil system.',
                effect: 'Controls Krog curvature. Default 3.0.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Default values',
            text: 'Default values are shown when gas-oil parameters are not overridden by measured data from the Kr Analysis page. All zones will show the same default values unless measured gas-oil Kr data was available during sample analysis.',
          },
        ],
      },
    ],
  },
  {
    id: 'krpc-workflow',
    label: 'Workflow',
    title: 'Step-by-Step Workflow',
    intro:
      'Follow these steps to validate Kr × Pc Model results and export them for simulator input.',
    sections: [
      {
        id: 'workflow-steps',
        title: 'Complete workflow',
        blocks: [
          {
            type: 'bullets',
            items: [
              '1. Verify upstream steps — ensure Capillary Pressure analyses (Liao or Brooks-Corey) and Kr analyses (Corey) are completed for representative samples.',
              '2. Verify zones have populated values — go to the Zones page and confirm zone_values are non-empty. If empty, re-run correlation on the Correlation page.',
              '3. Open the Kr × Pc Model page — curves are calculated automatically on page load.',
              '4. Select the model — toggle between Liao and Brooks-Corey to see which produces the most physically consistent Pc curves.',
              '5. Adjust curve resolution — increase to 100 points for smoother curves before exporting; use lower values for faster preview.',
              '6. Review the Pcow chart — confirm that zone ordering matches expected reservoir quality (high-FZI zones should have lower Pc).',
              '7. Review the Height chart — validate transition zone thickness for each zone against geological expectations.',
              '8. Review the Kr charts — check crossover points for wettability consistency and compare zone endpoints.',
              '9. Review the parameter tables — inspect individual zone_values. If a zone shows unrealistic values, return to the Zones page and adjust the zone mean or re-run correlation with revised sample selection.',
              '10. Export results — use the Export button to download Kr and Pc tables for simulator input.',
            ],
          },
        ],
      },
      {
        id: 'workflow-troubleshooting',
        title: 'Troubleshooting common issues',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'All charts blank',
                description: 'Zone_values are empty or zones have not been created.',
                effect: 'Go to the Zones page. If zone_values are empty, re-run correlation on the Correlation page with representative samples selected.',
              },
              {
                name: 'Pc curves out of order (low-FZI zone has lower Pc than high-FZI zone)',
                description: 'The FZI regression produced non-monotonic Pc_max or Pce values.',
                effect: 'Return to the Correlation page. Review the Pc_max and Pce regression fits — consider removing outlier samples or adjusting the zone mean values on the Zones page.',
              },
              {
                name: 'Kr curves do not reach expected endpoints',
                description: 'Krw\' or Kro\' values are outside expected ranges.',
                effect: 'Inspect the water-oil table. Trace the value back to the FZI regression on the Correlation page.',
              },
              {
                name: 'Gas-oil curves identical across all zones',
                description: 'All zones are using default gas-oil parameters.',
                effect: 'This is expected when no gas-oil Kr measurements are available. For differentiated gas-oil behavior, measured data from the Kr Analysis page is required.',
              },
            ],
          },
        ],
      },
    ],
  },
];
